import React, { useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { ArrowBackRounded, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import MainLayout from "../../../layouts/main_layout/main_layout";
import { useFeedback } from "../../../providers/feedback/feedback";
import { firestore } from "../../..";
import {
  deaneryList,
  parishListMap,
  ParishOption,
} from "../../../utils/deanery_parishes";
import { getYoucatQuizAuth } from "./quiz_auth";
import {
  getFirebaseAuthErrorMessage,
  getFriendlyServerErrorMessage,
} from "../../../utils/auth_error_messages";

const QUIZ_SETTINGS_COLLECTION = "youcatQuizSettings";
const QUIZ_SETTINGS_DOC_ID = "public";
const QUIZ_PASSWORD_RESET_ENDPOINT =
  "https://sendyoucatquizpasswordreset-f5v5da2yxa-uc.a.run.app";

type QuizSettings = {
  isQuizAvailable?: boolean;
  title?: string;
  description?: string;
};

export default function YoucatQuizAuthPage() {
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const quizAuth = useMemo(() => getYoucatQuizAuth(), []);
  const [activeTab, setActiveTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupParishes, setSignupParishes] = useState<ParishOption[]>([]);
  const [loginValues, setLoginValues] = useState({
    email: "",
    password: "",
  });
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [signupValues, setSignupValues] = useState({
    fullName: "",
    deanery: "",
    parish: "",
    email: "",
    contact: "",
    password: "",
    confirmPassword: "",
  });
  const [showSignupPassword, setShowSignupPassword] = useState(false);
  const [showSignupConfirmPassword, setShowSignupConfirmPassword] =
    useState(false);
  const [quizSettings, setQuizSettings] = useState<QuizSettings | null>(null);

  React.useEffect(() => {
    let isMounted = true;

    async function loadSettings() {
      try {
        const snapshot = await getDoc(
          doc(firestore, QUIZ_SETTINGS_COLLECTION, QUIZ_SETTINGS_DOC_ID),
        );
        if (isMounted) {
          setQuizSettings((snapshot.data() as QuizSettings | undefined) || {});
        }
      } catch (error) {
        console.error("Failed to load YouCat quiz settings", error);
        if (isMounted) {
          setQuizSettings({});
        }
      }
    }

    loadSettings();

    return () => {
      isMounted = false;
    };
  }, []);

  function validateEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  function validatePhone(phone: string) {
    return /^\d{10}$/.test(phone.trim());
  }

  async function handleLogin() {
    if (!loginValues.email.trim() || !loginValues.password) {
      showSnackbar("Please enter your email and password");
      return;
    }

    if (!validateEmail(loginValues.email)) {
      showSnackbar("Please enter a valid email address");
      return;
    }

    try {
      setIsSubmitting(true);
      await signInWithEmailAndPassword(
        quizAuth,
        loginValues.email.trim(),
        loginValues.password,
      );
      showSnackbar("Quiz login successful");
      navigate("/youcat/quiz/portal");
    } catch (error: any) {
      console.error("Failed to sign in to YouCat quiz", error);
      showSnackbar(
        getFirebaseAuthErrorMessage(error, "Failed to sign in. Please try again."),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleSignup() {
    if (
      !signupValues.fullName.trim() ||
      !signupValues.deanery.trim() ||
      !signupValues.parish.trim() ||
      !signupValues.email.trim() ||
      !signupValues.contact.trim() ||
      !signupValues.password ||
      !signupValues.confirmPassword
    ) {
      showSnackbar("Please fill all signup fields");
      return;
    }

    if (signupValues.password !== signupValues.confirmPassword) {
      showSnackbar("Password and confirm password must match");
      return;
    }

    if (!validateEmail(signupValues.email)) {
      showSnackbar("Please enter a valid email address");
      return;
    }

    if (!validatePhone(signupValues.contact)) {
      showSnackbar("Please enter a valid 10 digit contact number");
      return;
    }

    if (signupValues.password.length < 6) {
      showSnackbar("Password should be at least 6 characters");
      return;
    }

    try {
      setIsSubmitting(true);
      const credential = await createUserWithEmailAndPassword(
        quizAuth,
        signupValues.email.trim(),
        signupValues.password,
      );

      await setDoc(doc(firestore, "youcatQuizUsers", credential.user.uid), {
        uid: credential.user.uid,
        fullName: signupValues.fullName.trim(),
        deanery: signupValues.deanery.trim(),
        parish: signupValues.parish.trim(),
        email: signupValues.email.trim(),
        contact: signupValues.contact.trim(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });

      showSnackbar("Quiz signup completed successfully");
      navigate("/youcat/quiz/portal");
    } catch (error: any) {
      console.error("Failed to sign up for YouCat quiz", error);
      showSnackbar(
        getFirebaseAuthErrorMessage(
          error,
          "Failed to create quiz account. Please try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleForgotPassword() {
    if (!loginValues.email.trim()) {
      showSnackbar("Enter your email first to receive a reset link");
      return;
    }

    try {
      const response = await fetch(QUIZ_PASSWORD_RESET_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: loginValues.email.trim(),
        }),
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result?.error || "Failed to send reset email");
      }

      showSnackbar(result?.message || "Password reset email sent");
    } catch (error: any) {
      console.error("Failed to send YouCat quiz reset email", error);
      showSnackbar(
        getFriendlyServerErrorMessage(error, "Failed to send reset email"),
      );
    }
  }

  const isQuizAvailable = quizSettings?.isQuizAvailable !== false;

  return (
    <MainLayout>
      <Box my={{ xs: 2, md: 4 }}>
        <Button startIcon={<ArrowBackRounded />} onClick={() => navigate("/youcat")}>
          Back to YouCat
        </Button>

        <Card
          sx={{
            mt: 2,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 4,
            border: "1px solid rgba(217,119,6,0.12)",
            boxShadow: "0 24px 48px rgba(180, 120, 20, 0.08)",
          }}
        >
          <Typography variant="overline" sx={{ color: "#a16207", fontWeight: 800 }}>
            YouCat Quiz
          </Typography>
          <Typography variant="h4" fontWeight={900} color="#5c4100" mt={1}>
            {quizSettings?.title || "YouCat Quiz Login or Signup"}
          </Typography>
          <Typography color="#7c651d" mt={1}>
            {quizSettings?.description ||
              "Register once, sign in later, and continue to the quiz area when it is published."}
          </Typography>

          {!isQuizAvailable ? (
            <Alert severity="info" sx={{ mt: 3 }}>
              The YouCat Quiz is not available right now. You can come back after the admin publishes it.
            </Alert>
          ) : null}

          <Box mt={3} sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={activeTab} onChange={(_event, value) => setActiveTab(value)}>
              <Tab label="Login" />
              <Tab label="Signup" />
            </Tabs>
          </Box>

          {activeTab === 0 ? (
            <Box mt={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    value={loginValues.email}
                    onChange={(event) =>
                      setLoginValues((prev) => ({ ...prev, email: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showLoginPassword ? "text" : "password"}
                    label="Password"
                    value={loginValues.password}
                    onChange={(event) =>
                      setLoginValues((prev) => ({ ...prev, password: event.target.value }))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowLoginPassword((prev) => !prev)}>
                            {showLoginPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Stack direction={{ xs: "column", sm: "row" }} gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={handleLogin}
                  disabled={isSubmitting || !isQuizAvailable}
                >
                  {isSubmitting ? "Signing In..." : "Login"}
                </Button>
                <Button variant="text" onClick={handleForgotPassword}>
                  Forgot password?
                </Button>
                <Button variant="text" onClick={() => setActiveTab(1)}>
                  Not registered yet? Create account
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box mt={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    value={signupValues.fullName}
                    onChange={(event) =>
                      setSignupValues((prev) => ({ ...prev, fullName: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={deaneryList}
                    value={deaneryList.find((item) => item.name === signupValues.deanery) || null}
                    onChange={(_event, value) => {
                      setSignupValues((prev) => ({
                        ...prev,
                        deanery: value?.name || "",
                        parish: "",
                      }));
                      setSignupParishes(value ? parishListMap[value.name] || [] : []);
                    }}
                    getOptionLabel={(option) => option.name || ""}
                    renderInput={(params) => <TextField {...params} label="Deanery" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Autocomplete
                    fullWidth
                    options={signupParishes}
                    value={signupParishes.find((item) => item.value === signupValues.parish) || null}
                    onChange={(_event, value) =>
                      setSignupValues((prev) => ({ ...prev, parish: value?.value || "" }))
                    }
                    getOptionLabel={(option) => option.value || ""}
                    renderInput={(params) => <TextField {...params} label="Parish" />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    value={signupValues.email}
                    onChange={(event) =>
                      setSignupValues((prev) => ({ ...prev, email: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Contact"
                    value={signupValues.contact}
                    onChange={(event) =>
                      setSignupValues((prev) => ({ ...prev, contact: event.target.value }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showSignupPassword ? "text" : "password"}
                    label="Password"
                    value={signupValues.password}
                    onChange={(event) =>
                      setSignupValues((prev) => ({ ...prev, password: event.target.value }))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowSignupPassword((prev) => !prev)}>
                            {showSignupPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type={showSignupConfirmPassword ? "text" : "password"}
                    label="Confirm Password"
                    value={signupValues.confirmPassword}
                    onChange={(event) =>
                      setSignupValues((prev) => ({
                        ...prev,
                        confirmPassword: event.target.value,
                      }))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() =>
                              setShowSignupConfirmPassword((prev) => !prev)
                            }
                          >
                            {showSignupConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
              <Stack direction={{ xs: "column", sm: "row" }} gap={2} mt={3}>
                <Button
                  variant="contained"
                  onClick={handleSignup}
                  disabled={isSubmitting || !isQuizAvailable}
                >
                  {isSubmitting ? "Signing Up..." : "Signup"}
                </Button>
                <Button variant="text" onClick={() => setActiveTab(0)}>
                  Already registered?
                </Button>
              </Stack>
            </Box>
          )}
        </Card>
      </Box>
    </MainLayout>
  );
}
